import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarioGrid } from './CalendarioGrid';
import { Fab, Box, IconButton, Typography, CircularProgress, Chip, Stack, TextField, Button } from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import { getEstados, getOpciones, getRopa, getTareasCalendario, getTrabajadores } from '../../services/apis';
import { CrearEventoDialog } from '../eventos/CrearEventoDialog';

const formatCalendarDate = (date) => {
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();

	return `${day}-${month}-${year}`;
};

const formatDateInputValue = (date) => {
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const year = date.getFullYear();

	return `${year}-${month}-${day}`;
};

const parseApiDateToCalendarDate = (value) => {
	if (!value) {
		return '';
	}

	const [year, month, day] = value.split('-');
	return `${day}-${month}-${year}`;
};

const parseDateInputToCalendarDate = (value) => {
	if (!value) {
		return '';
	}

	const [year, month, day] = value.split('-');
	return `${day}-${month}-${year}`;
};

const parseCalendarDateToInput = (value) => {
	if (!value) {
		return '';
	}

	const [day, month, year] = value.split('-');
	return `${year}-${month}-${day}`;
};

export const Calendario = () => {
	const today = useMemo(() => new Date(), []);
	const [tareas, setTareas] = useState([]);
	const [anio, setAnio] = useState(today.getFullYear());
	const [mes, setMes] = useState(today.getMonth());
	const [fechaSalto, setFechaSalto] = useState(() => formatDateInputValue(today));
	const [diaPendienteScroll, setDiaPendienteScroll] = useState('');
	const [estados, setEstados] = useState([]);
	const [loaded, setLoaded] = useState(false);
	const [opTrabajadores, setOpTrabajadores] = useState([]);
	const [opRopa, setOpRopa] = useState([]);
	const [opciones, setOpciones] = useState(false);
	const [formOpen, setFormOpen] = useState(false);
	const [fechaSeleccionada, setFechaSeleccionada] = useState(null);

	const todayId = formatCalendarDate(today);
	const isCurrentMonth = mes === today.getMonth() && anio === today.getFullYear();

	const tareasDelMes = useMemo(() => {
		return tareas.filter((tarea) => {
			const [, taskMonth, taskYear] = String(tarea.fecha || '').split('-').map(Number);
			return taskMonth === mes + 1 && taskYear === anio;
		});
	}, [anio, mes, tareas]);

	const nombreMes = useMemo(() => (
		new Date(anio, mes).toLocaleString('ca-ES', { month: 'long' })
	), [anio, mes]);

	const cargarTrabajadores = useCallback(async () => {
		try {
			const response = await getTrabajadores();
			const trabajadores = (response?.data || []).map((item) => ({
				label: item.nombre,
				value: item.id,
			}));

			setOpTrabajadores(trabajadores);
		} catch (error) {
			console.error('Error en carregar usuaris:', error);
		}
	}, []);

	const cargarRopa = useCallback(async () => {
		try {
			const response = await getRopa();
			const prendas = (response?.data || []).map((item) => ({
				...item,
				label: item.nombre,
				value: item.id,
			}));

			setOpRopa(prendas);
		} catch (error) {
			console.error('Error en carregar roba:', error);
		}
	}, []);

	const cargarOpciones = useCallback(async () => {
		try {
			const response = await getOpciones();
			const opcionesResponse = response?.data ?? response?.opciones ?? false;
			setOpciones(opcionesResponse === true || Number(opcionesResponse) === 1);
		} catch (error) {
			console.error('Error en carregar opcions:', error);
		}
	}, []);

	const cargarEstados = useCallback(async () => {
		try {
			const response = await getEstados();
			setEstados(response?.data || []);
		} catch (error) {
			console.error('Error en carregar estats:', error);
		}
	}, []);

	const tareasCalendario = useCallback(async () => {
		try {
			const response = await getTareasCalendario();
			const eventos = (response?.data || []).map((tarea) => ({
				...tarea,
				fecha: parseApiDateToCalendarDate(tarea.fecha),
			}));

			setTareas(eventos);
			setLoaded(true);
		} catch (error) {
			console.error('Error en carregar esdeveniments:', error);
			setLoaded(true);
		}
	}, []);

	useEffect(() => {
		tareasCalendario();
		cargarEstados();
		cargarTrabajadores();
		cargarRopa();
		cargarOpciones();
	}, [cargarEstados, cargarOpciones, cargarRopa, cargarTrabajadores, tareasCalendario]);

	useEffect(() => {
		if (!loaded || !isCurrentMonth) {
			return;
		}

		const todayCell = document.getElementById(todayId);
		todayCell?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, [isCurrentMonth, loaded, todayId]);

	useEffect(() => {
		if (!loaded || !diaPendienteScroll) {
			return;
		}

		const timer = setTimeout(() => {
			document.getElementById(diaPendienteScroll)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			setDiaPendienteScroll('');
		}, 50);

		return () => clearTimeout(timer);
	}, [diaPendienteScroll, loaded, mes, anio]);

	const cambiarMes = (delta) => {
		const nextDate = new Date(anio, mes + delta, 1);
		setMes(nextDate.getMonth());
		setAnio(nextDate.getFullYear());
	};

	const saltarAFecha = (event) => {
		const value = event.target.value;
		setFechaSalto(value);

		if (!value) {
			return;
		}

		const [year, month] = value.split('-').map(Number);
		setMes(month - 1);
		setAnio(year);
		setDiaPendienteScroll(parseDateInputToCalendarDate(value));
	};

	const irAHoy = () => {
		setMes(today.getMonth());
		setAnio(today.getFullYear());

		setTimeout(() => {
			document.getElementById(todayId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}, 50);
	};

	const abrirFormularioCrearEvento = (fecha = null) => {
		setFechaSeleccionada(fecha || parseDateInputToCalendarDate(fechaSalto) || todayId);
		setFormOpen(true);
	};

	const cerrarFormularioCrearEvento = () => {
		setFormOpen(false);
		setFechaSeleccionada(null);
	};

	return (
		<div className="calendar-shell">
			<Box className="calendar-toolbar">
				<Stack direction="row" className="calendar-toolbar-title-block">
					<IconButton className="calendar-nav-button" onClick={() => cambiarMes(-1)} aria-label="Mes anterior">
						<ArrowBackIos fontSize="small" />
					</IconButton>
					<Box className="calendar-toolbar-copy">
						<Typography className="calendar-eyebrow">
							Calendari
						</Typography>
						<Typography variant="h5" className="calendar-toolbar-title">
							{nombreMes} {anio}
						</Typography>
					</Box>
				</Stack>

				<Stack direction="row" className="calendar-toolbar-actions">
					<TextField
						className="calendar-jump-date"
						label="Saltar a dia"
						type="date"
						size="small"
						value={fechaSalto}
						onChange={saltarAFecha}
						InputLabelProps={{ shrink: true }}
					/>
					{loaded && (
						<Chip className="calendar-stat" label={`${tareasDelMes.length} esdeveniments`} />
					)}
					{opciones === true && (
						<Button
							className="calendar-add-button"
							variant="contained"
							startIcon={<AddIcon />}
							onClick={() => abrirFormularioCrearEvento()}
						>
							Afegir
						</Button>
					)}
					<IconButton className="calendar-nav-button calendar-next-button" onClick={() => cambiarMes(1)} aria-label="Mes següent">
						<ArrowForwardIos fontSize="small" />
					</IconButton>
					<Fab className="calendar-today-button" size="small" color="primary" onClick={irAHoy} aria-label="Hui">
						<TodayIcon />
					</Fab>
				</Stack>
			</Box>

			{!loaded && (
				<Box className="calendar-loading">
					<CircularProgress size={40} />
				</Box>
			)}

			{loaded && (
				<CalendarioGrid
					todayId={todayId}
					mesActual={mes}
					anioActual={anio}
					tareas={tareas}
					estados={estados}
					opTrabajadores={opTrabajadores}
					opciones={opciones}
					abrirFormularioCrearEvento={abrirFormularioCrearEvento}
				/>
			)}
			{opciones === true && (
				<CrearEventoDialog
					open={formOpen}
					onClose={cerrarFormularioCrearEvento}
					opTrabajadores={opTrabajadores}
					opRopa={opRopa}
					onCreated={tareasCalendario}
					fechaInicial={parseCalendarDateToInput(fechaSeleccionada)}
				/>
			)}
		</div>
	);
};
